import "custom-event-polyfill";

import * as App from "@timesheets/routes-app";

import { ApiErrorEvent, IApiResponse } from "@gemma/api-common";
import { DateRelativeTo } from "@gemma/utils";
import { Events, IById, IWebApplication, Events as WebAppEvents } from "@timesheets/webapp-support";
import { LogoTypeNames, RecruitmentAgency, Session, SquareName, TRecruitmentAgency } from "@timesheets/constants-common";

import { ISettingSetEvent } from "@gemma/entities";
import { IState } from "../vuex/IState";
import { Store } from "vuex";
import { ThisTypedComponentOptionsWithArrayProps } from "vue/types/options";
import Vue from "vue";
import { Plugin as VueSlideout } from "@/plugins/vue-slideout";
import { WebApplicationConstants } from "../setup/WebApplicationConstants";
import { logout } from "./logout";
import { setFavIco } from "./setFavIco";
import { setTenantColors } from "./setTenantColors";
import { vuePrototype } from "@/app/mixins";
import { IEvent } from "@gemma/events";
import { VueConstructor } from "vue/types/umd";
import { getLogoPathFromType } from "./getLogoPathFromType";
import { Day } from "@gemma/dates";

export async function createVue<VC extends VueConstructor>(webApplication: IWebApplication, view: VC, options: ThisTypedComponentOptionsWithArrayProps<Vue, object, object, object, never> = {}) {
  let vue: Vue;
  const store = options.store as Store<IState>;
  WebApplicationConstants.eventBus.createFilter(Session.LogoutLocal).addSubscription(async () => await logout(webApplication.api, vue.$router));

  WebApplicationConstants.eventBus
    //
    .createFilters([RecruitmentAgency.SetBrandingColors, RecruitmentAgency.ResetBrandingColors])
    .addSubscription((event: IEvent<{ colors: { all: TRecruitmentAgency["TBrandingColors"] }; tenantInfo: { canonicalTenantName: string } }, string>) => {
      const tenantName = event.data.tenantInfo.canonicalTenantName;
      const roles = store.state.user.roles.filter(role => role.recruitmentAgency.names.currentCanonical === tenantName);
      roles.forEach(role => (role.recruitmentAgency.settings.colors = event.data.colors.all));
      setTenantColors(tenantName, event.data.colors.all);
    });

  WebApplicationConstants.eventBus.createFilter(ApiErrorEvent).addSubscription(async (apiErrorEvent: ApiErrorEvent<IApiResponse<unknown>, string>) => {
    if (apiErrorEvent.data.meta.type === "notFound") {
      vue.$router.push(`${App.NotFound}?url=${encodeURI(apiErrorEvent.data.meta.route)}`).catch(() => {});
    } else if (apiErrorEvent.data.meta.type === "methodNotAllowed") {
      WebApplicationConstants.defaultLogger.error(apiErrorEvent.toGemmaError());
    } else if (apiErrorEvent.data.meta.type === "authorizationRequired") {
      await logout(webApplication.api, vue.$router);
    } else if (apiErrorEvent.data.meta.type === "serviceUnavailable") {
      WebApplicationConstants.defaultLogger.error(apiErrorEvent.toGemmaError());
      // vue.$router.push(`${ServiceUnavailable}?url=${encodeURI(apiErrorEvent.data.data.status)}`).catch(() => {});
    } else {
      WebApplicationConstants.defaultLogger.error(apiErrorEvent.toGemmaError());
    }
  });

  WebApplicationConstants.eventBus.createFilters([RecruitmentAgency.SetSquareLogo, RecruitmentAgency.DeleteSquareLogo, RecruitmentAgency.SetRectangularLogo, RecruitmentAgency.DeleteRectangularLogo]).addSubscription(async (event: IEvent<{ canonicalTenantName: string; logoType: LogoTypeNames }, string>) => {
    const tenantName = event.data.canonicalTenantName;
    store.dispatch("updateLogoTimestamp", { logoType: event.data.logoType, tenantName });
    if (WebApplicationConstants.currentTenant === event.data.canonicalTenantName) {
      const logoPath = getLogoPathFromType(event.data.logoType);
      let logoChangedEvent: IEvent<{ logoPath: string }, string>;
      if (event.data.logoType === SquareName) {
        logoChangedEvent = new Events.SquareLogoChangedEvent(logoPath, tenantName);
      } else {
        logoChangedEvent = new Events.RectangularLogoChangedEvent(logoPath, tenantName);
      }
      await WebApplicationConstants.eventBus.publish(logoChangedEvent);
    }
  });

  WebApplicationConstants.eventBus.createFilter(Events.SquareLogoChangedEvent.Type).addSubscription((logoChangedEvent: Events.SquareLogoChangedEvent) => {
    if (logoChangedEvent.data.tenantName === WebApplicationConstants.currentTenant) {
      setFavIco(store);
    }
  });
  setFavIco(store);

  WebApplicationConstants.eventBus
    //
    .createFilter(RecruitmentAgency.SetSetting)
    .addSubscription(async (event: ISettingSetEvent<any, string> & { data: { recruitmentAgency: IById } }) => {
      let value = event.data.value;
      let internalEvent;

      switch (event.data.path) {
        case RecruitmentAgency.Settings.Defaults.DateRelativeTo.path:
          value = DateRelativeTo.parse(value);
          break;

        case RecruitmentAgency.Settings.Defaults.TimesheetWeekStartsOn.path:
          value = Day.parse(value);
          break;

        case RecruitmentAgency.Settings.contractorFinancials.path:
          internalEvent = new WebAppEvents.AppEvent("contractorFinancials", value);
          break;

        default:
          break;
      }

      const changedRoles = store.state.user.roles
        .filter(role => role.recruitmentAgency.id === event.data.recruitmentAgency.id)
        .map(role => {
          let child = role.recruitmentAgency.settings;
          const subSettings = event.data.path.split(".");
          const finalSetting = subSettings.pop();
          subSettings.forEach(current => {
            if (!(<any>child)[current]) {
              (<any>child)[current] = {};
            }
            child = (<any>child)[current];
          });

          (<any>child)[finalSetting as string] = value;

          return role;
        });

      if (store.state.user.currentRole) {
        const id = store.state.user.currentRole.id;
        const _currentRole = changedRoles.find(role => role.id === id);
        if (_currentRole) {
          store.dispatch("user/setCurrentRole", { role: _currentRole });
        }
      }

      if (internalEvent) {
        WebApplicationConstants.eventBus.publish(internalEvent);
      }
    });
  Vue.use(VueSlideout);

  vuePrototype(webApplication);
  WebApplicationConstants.listenerEventBuses.system.on(Events.AppMountedEvent.Type, async function() {
    Vue.nextTick(() => Vue.prototype.$vueSlideout.mountRoot("body"));
  });

  options.render = getCreateElement => getCreateElement(view);
  vue = new Vue(options).$mount("#app");

  WebApplicationConstants.defaultLogger.info(WebApplicationConstants.systemInformation);

  return vue;
}
